import React, {ReactDOM, useState, useContext, useEffect, useMemo, useCallback } from "react";
import { AddEditInventory } from "./lazyForms";
import VehiclePickerModal from "./VehiclePickerModal";
import EnrichVehiclesModal from "./EnrichVehiclesModal";
import ListingHealthBanner from "./ListingHealthBanner";
import { useListingHealth } from "./hooks/useListingHealth";
import { consumeEnrich } from "./enrichIntent";
import { useQueryClient } from "@tanstack/react-query";
import utilities, { readJsonDraft } from "./UtilityFunctions";
import CommonDataView from "./CommonDataView";
import { GlobalContext } from "./GlobalContext";
import fields from "./fields";
import { Button } from "@radix-ui/themes";
import { AddIcon, ICON_SIZE } from "./actionIcons";
import { Sparkles } from "lucide-react";
import { useIsMobile } from "./hooks/useMediaQuery";
//Lazy (plan 5.1): keeps recharts (DonutChart's dependency) out of the entry
//chunk; the donut is desktop-only so phones never fetch it. The render site
//must gate on useIsMobile — a CSS `hidden` div still mounts, which would
//trigger the import.
const Donut = React.lazy(() => import('./InventoryDonut'));
import useTabularDataManager from "./useTabularDataManager";
import useFieldOrder from "./useFieldOrder";
import { emitter } from "./UtilityFunctions";
import FloatingDownloadButton from "./FloatingDownloadButton";
import AiButton from "./components/AiButton";
import DensityToggle from "./components/DensityToggle";
import { describeBatch } from "./aiApi";
import { useToast } from "./ToastContext";
import { TOUR_ANCHORS } from "./tourAnchors";
import SheetFilterChips from "./components/SheetFilterChips";

function Inventory({ cars, setCars, initialSelectedRow = "" , isLoading = false }){
    //Declared BEFORE the hook that now takes it. It used to sit on the line
    //after, which was harmless while nothing read it there and is a temporal-
    //dead-zone crash the moment something does.
    const ownSheetName = "Inventory";
    const {
        modalVisible, setModalVisibility,
        sortOrder, sortData,
        filterObject, filterData,
        filteredDataArray
        } = useTabularDataManager(cars, setCars, ownSheetName);
    const [prefillData, setPrefillData] = useState(null);

    return (
        <div className="flex flex-col h-full sheet-viewport" id="inventory-content">
            {/* Unwrapped. The banner returns null for most people most of the
                time — no inventory.edit, nothing incomplete, or dismissed for
                seven days — but its `flex-none px-4 pt-3` wrapper rendered
                either way, leaving 16px of blank above the table. It also inset
                the banner 24px while `sheet-table` below it starts flush at 0.
                The banner's own `margin: 0 0 12px` is all the spacing it needs.

                Both values were Bootstrap's, not Tailwind's: bootstrap-compat
                redefines px-4 and pt-3 with `!important`. */}
            <ListingHealthBanner sheetName={ownSheetName} />
            {/* Above the scroll container, not inside it: that container scrolls
                horizontally with the table, so chips placed within it would slide
                off the side exactly like the column header they replace. Desktop
                only — MobileSheetView renders the same component in its toolbar. */}
            <SheetFilterChips
                filterObject={filterObject}
                filterData={filterData}
                className="hidden md:flex"
            />
            <Content isLoading={isLoading}
                modalVisible={modalVisible}
                setModalVisibility={setModalVisibility}
                filteredDataArray={filteredDataArray} setCars={setCars} sortData={sortData}
                sortOrder={sortOrder} parentSheetName={ownSheetName}
                filterData={filterData} filterObject={filterObject}
                ownSheetName={ownSheetName}
                initialSelectedRow={initialSelectedRow}
                prefillData={prefillData}
            />
            <Summary
                setModalVisibility={setModalVisibility}
                setPrefillData={setPrefillData}
                filteredDataArray={filteredDataArray}
            />
        </div>
    )
}

function Content({ filteredDataArray, setCars, modalVisible, setModalVisibility,
     sortData, sortOrder, parentSheetName, filterData, filterObject, ownSheetName, initialSelectedRow = "", prefillData, isLoading }){
    const [fieldOrder, setFieldOrder] = useFieldOrder(parentSheetName, filteredDataArray[0], ['Make', 'Model', 'ModelYear', 'NumberPlate', 'Days']);
    const [ currentVehicleIndex, setCurrentVehicle ] = useState();
    const openModalEdit = (vehicleId)=>{
        setCurrentVehicle(vehicleId);
        setModalVisibility({
            visible: true,
            mode: "edit"
        });
    }
    const [ selectedRow, setSelectedRow ] = useState(initialSelectedRow)

    useEffect(() => {
        if (initialSelectedRow) setSelectedRow(initialSelectedRow);
    }, [filteredDataArray, initialSelectedRow]);

    const selectRow = (rowIndex)=>{
        setSelectedRow(rowIndex)
    }

    // Closures reference no component state — stable for the component lifetime.
    const formattingRules = useMemo(() => ({
        Days: {
            formatByValue: (val)=>{
                if(val > 240 && val <= 270){
                    return {color: "orange", fontWeight: "bold",textAlign: "center"}
                }
                if(val > 270){
                    return {color: "red", fontWeight: "bold",textAlign: "center"}
                }
                return {color: "green", fontWeight: "bold",textAlign: "center"}
            }
        }
    }), []);

    // Row formatting disabled for now; useCallback keeps the ref stable.
    const rowFormattingRule = useCallback((_row) => {
        return undefined; //Disable row formatting for now, as it is causing aesthetic issues. Will optimize and re-enable in the future
    }, []);

    return (
        <div className="flex-grow overflow-auto min-h-0">
            <CommonDataView
                isLoading={isLoading}
                objectsArray={filteredDataArray}
                sortData={sortData}
                sortOrder={sortOrder}
                fieldOrder={fieldOrder}
                setFieldOrder={setFieldOrder}
                parentSheetName={parentSheetName}
                filterData={filterData}
                filterObject={filterObject}
                sheetName={ownSheetName}
                selectRow={selectRow}
                selectedRow={selectedRow}
                openModalEdit={openModalEdit}
                primaryKeyName={"VehicleID"}
                formattingRules={formattingRules}
                rowFormattingRule={rowFormattingRule}
                searchPlaceholder="Search vehicles…"
            />
            {
                modalVisible.visible &&  (modalVisible.mode==="edit" ? (filteredDataArray[currentVehicleIndex] || false) : true) &&
                <AddEditInventory
                    formMode={modalVisible.mode}
                    showModal={setModalVisibility}
                    car={filteredDataArray[currentVehicleIndex]}
                    parentSheetName={parentSheetName}
                    //In add mode, read the origin that the user selected. In edit mode, read the origin from the record's origin field
                    origin={modalVisible.mode==="add" ? modalVisible.origin : filteredDataArray[currentVehicleIndex].Origin}
                    prefillData={modalVisible.mode==="add" ? prefillData : null}
                    //The sheet as the user currently has it filtered and sorted.
                    //The Summary tab answers questions that only exist relative
                    //to the rest of the stock — how old this unit is against the
                    //fleet, how its asking price compares to similar units — and
                    //there is no endpoint for that; the array is already here.
                    //It also gives the header its record stepper, so moving to
                    //the next vehicle no longer means closing the modal.
                    fleet={filteredDataArray}
                    fleetIndex={currentVehicleIndex}
                    onStep={setCurrentVehicle}
                />
            }
            <FloatingDownloadButton 
                data={filteredDataArray}
                fieldOrder={fieldOrder}
                sheetName={ownSheetName}
                filterObject={filterObject}
            />     
        </div>
    )
}

function Summary({ setModalVisibility, setPrefillData, filteredDataArray}){
    const { getMsgBoxResult, hiddenCostFields, toggleMessageBox, can, UserID } = useContext(GlobalContext);
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const isMobile = useIsMobile();
    //A user who can't see a REQUIRED field can't supply it when adding a
    //vehicle, so block adding entirely (Phase 8 cost visibility).
    const restrictedRequired = [...(hiddenCostFields || [])].filter((f) => fields[f]?.required);
    const canAddVehicle = restrictedRequired.length === 0;
    const restrictedNames = restrictedRequired.map((f) => fields[f].displayName).join(', ');
    const [ data, setData ] = useState([]);
    const [ pickerState, setPickerState ] = useState({ visible: false, origin: null });
    const [ enrichOpen, setEnrichOpen ] = useState(false);

    //How many vehicles are missing something the dealership's public website
    //filters on, so the button can say what it will find before anyone presses
    //it — a button labelled only "Fill specs" gives no reason to press it, and
    //this is a chore nobody goes looking for.
    //
    //TENANT-WIDE, from the server. This used to count filteredDataArray, which
    //is the loaded page: the sheet paginates at 50, so on any dealership with
    //more stock than that the button under-reported while the review screen it
    //opens — which has always read the whole tenant — showed more. The button
    //and the screen behind it now read the same number from the same place.
    const { data: listingHealth } = useListingHealth({ enabled: can('inventory.edit') });
    const incompleteCount = listingHealth?.incomplete || 0;
    
    //Emit an 'inventory-loaded' event when the component mounts
    useEffect(() => {
        emitter.emit('inventory-loaded');
    }, []);

    //The post-save toast in AddEditInventory offers a one-click "Fill specs",
    //but that modal lives here. An event rather than a shared context because
    //this is a one-off nudge, not state either component needs to hold.
    useEffect(() => {
        const open = () => setEnrichOpen(true);
        emitter.on('open-enrich-specs', open);
        //The banner on the dashboard cannot use that event — nothing is
        //listening until this component exists. It leaves a flag instead, which
        //is read here on the way in.
        if (consumeEnrich()) setEnrichOpen(true);
        return () => emitter.off('open-enrich-specs', open);
    }, []);
    
    //Update the data for the Donut chart whenever the filteredDataArray changes
    useEffect(()=>{
        const numberOfCars = filteredDataArray.length;
        const numberOfLocal = filteredDataArray.filter((car)=>car.Origin==="Local").length;
        const numberOfImported = numberOfCars - numberOfLocal;
        const data = [];
        //Reduce data to return an array with total number of vehicles and total value, split into categories "Local" and "Imported"
        data.push({
            "name": "Local",
            "numbers": numberOfLocal,
            "share": `${utilities.formatIntoInput((numberOfLocal/numberOfCars*100).toFixed(0), "decimal")} %`,
            href: '#',
        });
        data.push({
            "name": "Imported",
            "numbers": numberOfImported,
            //No borderColor: the legend swatch takes its colour from the chart
            //theme inside InventoryDonut, so the arc and the swatch cannot
            //disagree. A class name written here could only ever go stale.
            "share": `${utilities.formatIntoInput((numberOfImported/numberOfCars*100).toFixed(0), "decimal")} %`,
            href: '#',
        });
        setData(data);
    },[filteredDataArray]);
    
    const addNewVehicle = async ()=>{
        if (!canAddVehicle) {
            toggleMessageBox({
                on: true,
                title: "Can't add a vehicle",
                message: `Adding a vehicle requires ${restrictedNames}, which you don't have permission to set. Ask someone with cost access to add it.`,
                buttons: ["OK"],
            });
            return;
        }
        const origin = await getMsgBoxResult({
            on: true,
            title: "What Is The Vehicle's Origin",
            message: "Is the vehicle Ugandan Used (Local), or in the bond (imported)?",
            buttons: ["UG Used (Local)", "Bond (Imported)"]
        });
        const resolvedOrigin = origin === "UG Used (Local)" ? "Local" : "Imported";

        localStorage.removeItem(`tmpNewInventory${resolvedOrigin}`); //purge legacy unscoped drafts
        const entry = readJsonDraft(`tmpNewInventory${resolvedOrigin}_u${UserID}`);
        if (entry) {
            const lines = [
                entry.Make       && `Make: ${entry.Make}`,
                entry.Model      && `Model: ${entry.Model}`,
                entry.ChassisNo  && `Chassis: ${entry.ChassisNo}`,
                entry.NumberPlate && `Plate: ${entry.NumberPlate}`,
            ].filter(Boolean);
            const detail = lines.length ? `\n\n${lines.join("\n")}` : "";
            const choice = await getMsgBoxResult({
                on: true,
                title: "Unfinished Entry Found",
                message: `You have an unfinished ${resolvedOrigin} vehicle entry saved.${detail}\n\nContinue where you left off, or discard it and start fresh?`,
                buttons: ["Continue previous", "Start fresh"]
            });
            if (choice === "Continue previous") {
                // Skip the wizard — go straight to the form with the saved data
                setPickerState({ visible: false, origin: null });
                setPrefillData(null);
                setModalVisibility({ visible: true, mode: "add", origin: resolvedOrigin });
                return;
            }
            localStorage.removeItem(`tmpNewInventory${resolvedOrigin}_u${UserID}`);
        }

        setPickerState({ visible: true, origin: resolvedOrigin });
    }

    const openInventoryModal = (origin, prefill = null) => {
        setPrefillData(prefill);
        setPickerState({ visible: false, origin: null });
        setModalVisibility({ visible: true, mode: "add", origin });
    };

    const handleTutorial = () => {
        // Add your tutorial logic here
        console.log("Starting inventory tutorial");
    }
    
    return (
        <>
            <div className="h-14 md:h-[80px] flex-none bg-white border-t flex items-center px-4 gap-2 relative">
                {can('inventory.add') && (
                    <Button
                        variant="soft"
                        color="green"
                        size="2"
                        radius="medium"
                        onClick={addNewVehicle}
                        title={canAddVehicle ? "" : `You can't add vehicles: required fields (${restrictedNames}) are restricted for your role.`}
                        id={TOUR_ANCHORS.inventoryAddNew}
                    ><AddIcon size={ICON_SIZE} /> Add New</Button>
                )}

                {/* Fill missing catalog specs from the carmakes lookup
                    (VEHICLE_DATA_QUALITY_PLAN.md Part 1). Gated on
                    inventory.edit because that is what applying it does — the
                    review screen itself is readable by anyone, but there is no
                    point opening it without the permission to act on it. */}
                {can('inventory.edit') && incompleteCount > 0 && (
                    <Button
                        id="inventory-fill-specs"
                        variant="soft"
                        size="2"
                        radius="medium"
                        onClick={() => setEnrichOpen(true)}
                        title="Fill in missing specs from the vehicle lookup — the same source the add-vehicle wizard uses"
                    >
                        <Sparkles size={15} /> Fill specs ({incompleteCount})
                    </Button>
                )}

                {/* AI batch description backfill — gated on records.import.
                    Sends at most 50 IDs; server skips vehicles with existing
                    descriptions. Shows a per-result toast summary on completion. */}
                {can('records.import') && (
                    <AiButton
                        label="Generate missing descriptions"
                        onGenerate={async () => {
                            // Collect vehicle IDs from loaded sheet data.
                            // Filter client-side to ones with empty description when
                            // available; otherwise send all (server skips non-empty).
                            const allIds = filteredDataArray
                                .filter((v) => !v.Description)
                                .map((v) => v.VehicleID);
                            const MAX_BATCH = 50;
                            if (allIds.length === 0) {
                                showToast('info', 'All loaded vehicles already have descriptions.');
                                return;
                            }
                            const batch = allIds.slice(0, MAX_BATCH);
                            const skippedCount = allIds.length - batch.length;
                            const { results } = await describeBatch({ vehicleIds: batch });
                            const done  = results.filter((r) => r.status === 'done').length;
                            const exists = results.filter((r) => r.status === 'exists').length;
                            const errors = results.filter((r) => r.status === 'error').length;
                            const parts = [
                                done    && `${done} generated`,
                                exists  && `${exists} already had descriptions`,
                                errors  && `${errors} failed`,
                                skippedCount && `${skippedCount} not sent (batch cap 50)`,
                            ].filter(Boolean).join(', ');
                            if (errors > 0) {
                                showToast('warning', `Descriptions: ${parts}`);
                            } else {
                                showToast('success', `Descriptions: ${parts}`);
                            }
                        }}
                    />
                )}

                {/* The donut needs its fixed 400px card — desktop only. JS
                    gate, not CSS: a hidden mount would still pull the lazy
                    recharts chunk on phones. */}
                {!isMobile && (
                    <React.Suspense fallback={<div style={{ width: '400px', margin: '0px 20px' }} className="h-20" />}>
                        <Donut data={data} />
                    </React.Suspense>
                )}

                {/* Roomy/compact rows. The six sheets that render SheetFooter
                    have had this in their bottom row since the toggle was
                    written; Inventory, Committed and Credit keep a stats band
                    instead of that footer and so were the three that went
                    without. Pinned right on all three, which is the one position
                    that survives each band having different contents.

                    `hidden md:flex`, like the footer's own row: it resizes table
                    rows, and below 768px CommonDataView renders cards. */}
                <div className="hidden md:flex items-center ml-auto pr-1">
                    <DensityToggle id="inventory-density-toggle" />
                </div>
            </div>

            {pickerState.visible && (
                <VehiclePickerModal
                    origin={pickerState.origin}
                    onComplete={(prefill) => openInventoryModal(pickerState.origin, prefill)}
                    onSkip={() => openInventoryModal(pickerState.origin, null)}
                    onClose={() => setPickerState({ visible: false, origin: null })}
                />
            )}

            {/* Mounted only while open: the modal fetches on mount and this
                keeps a screen nobody asked for from issuing a request. */}
            {enrichOpen && (
                <EnrichVehiclesModal
                    open={enrichOpen}
                    onOpenChange={setEnrichOpen}
                    //The server broadcasts a refetch to the tenant's sockets, so
                    //the sheet updates itself; this only closes the loop for a
                    //client whose socket dropped.
                    //
                    //The health query is invalidated explicitly because the
                    //socket refetch does not touch it: leaving it cached would
                    //leave the banner and the button quoting the count from
                    //before the fix, which is the one moment the user is
                    //looking straight at them.
                    onApplied={() => {
                        emitter.emit('inventory-loaded');
                        queryClient.invalidateQueries({ queryKey: ['listing-health'] });
                    }}
                />
            )}
        </>
    )
}

export default Inventory;